/**
 * Local fallback product data used when the Shopify Storefront API is
 * unavailable (e.g. HTTP 402 "Unavailable Shop" while the dev store is
 * unbilled). Keeps PDPs and the shop grid renderable from local copy +
 * hero imagery, with checkout disabled. Once Shopify responds normally
 * again, the loader skips this path automatically.
 */
import type { ShopifyProduct } from "@/lib/shopify";
import { getPackPricingTable } from "@/lib/checkout-pricing";
import liverHero__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const liverHero = liverHero__asset.url;
import magHero__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const magHero = magHero__asset.url;
import ovaHero__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const ovaHero = ovaHero__asset.url;

type FallbackDef = {
  handle: string;
  title: string;
  description: string;
  productType: string;
  tags: string[];
  image: string;
  imageAlt: string;
};

const FALLBACKS: FallbackDef[] = [
  {
    handle: "mag-absorb-pro",
    title: "Mag Absorb Pro",
    description:
      "5-form magnesium matrix with liposomal bisglycinate + Aquamin® for deeper sleep, calmer nerves and faster recovery. Research-backed doses, vegan, third-party lab tested.",
    productType: "Supplement",
    tags: ["sleep", "stress", "recovery"],
    image: magHero,
    imageAlt: "TruWe Mag Absorb Pro bottle",
  },
  {
    handle: "liver-biome-plus",
    title: "Liver Biome Plus",
    description:
      "Liposomal liver & gut support with curcumin, silymarin, ALA and NAC plus a 10B CFU multi-strain probiotic. Targets bloating, fatigue and daily detox at the source.",
    productType: "Supplement",
    tags: ["liver", "gut", "detox"],
    image: liverHero,
    imageAlt: "TruWe Liver Biome Plus bottle",
  },
  {
    handle: "ova-balance",
    title: "Ova Balance",
    description:
      "Daily PCOS support for a more consistent routine. Ova Balance combines Myo- and D-Chiro Inositol in a 40:1 ratio with Quatrefolic® L-5-MTHF, Berberine HCl and Vitamin D3 600 IU (15 mcg) per sachet —thoughtfully formulated to support cycle, skin and metabolic wellness as part of your broader PCOS-care plan.",
    productType: "Supplement",
    tags: ["pcos", "hormones", "women"],
    image: ovaHero,
    imageAlt: "TruWe Ova Balance bottle",
  },
];

/**
 * Single-pack price for a fallback product. Comes from the shared pricing
 * registry (code defaults + prices published from Admin → Products), so no
 * price is hardcoded in this file.
 */
function singlePack(handle: string): { price: number; mrp: number } {
  return getPackPricingTable(handle)?.[1] ?? { price: 0, mrp: 0 };
}

// Alias handles → canonical fallback
const ALIASES: Record<string, string> = {
  "liver-biome": "liver-biome-plus",
  "liver-biome-": "liver-biome-plus",
  "mag-absorb": "mag-absorb-pro",
};

function toShopifyShape(def: FallbackDef, requestedHandle: string): ShopifyProduct["node"] {
  const variantId = `gid://truwe-fallback/ProductVariant/${def.handle}`;
  return {
    id: `gid://truwe-fallback/Product/${def.handle}`,
    title: def.title,
    description: def.description,
    handle: requestedHandle,
    productType: def.productType,
    tags: def.tags,
    priceRange: { minVariantPrice: { amount: singlePack(def.handle).price.toFixed(2), currencyCode: "INR" } },
    compareAtPriceRange: {
      minVariantPrice: { amount: singlePack(def.handle).mrp.toFixed(2), currencyCode: "INR" },
    },
    images: { edges: [{ node: { url: def.image, altText: def.imageAlt } }] },
    variants: {
      edges: [
        {
          node: {
            id: variantId,
            title: "Default",
            price: { amount: singlePack(def.handle).price.toFixed(2), currencyCode: "INR" },
            compareAtPrice: { amount: singlePack(def.handle).mrp.toFixed(2), currencyCode: "INR" },
            availableForSale: false,
            selectedOptions: [{ name: "Title", value: "Default" }],
          },
        },
      ],
    },
  };
}

export function getFallbackProduct(handle: string): ShopifyProduct["node"] | null {
  const normalized = ALIASES[handle] ?? handle;
  const def = FALLBACKS.find((f) => f.handle === normalized);
  return def ? toShopifyShape(def, handle) : null;
}

export function getFallbackProductList(): ShopifyProduct[] {
  return FALLBACKS.map((def) => ({ node: toShopifyShape(def, def.handle) }));
}
